<script lang="ts">
	import { createEventDispatcher, onMount, onDestroy } from "svelte";
	import { fade, fly } from "svelte/transition";
	import NewsList from "./NewsList.svelte";
	import {
		formatNumber,
		formatShortDate,
		currencyDecimals,
		CURRENCY_NAMES,
		FUEL_LABELS,
		INDEX_LABELS,
		INDEX_DESCRIPTIONS,
		NEWS_LIMIT,
		trackFinance,
	} from "./finance.js";

	export let domain;
	export let brandName = "Adevărul";
	export let brandLogo = "/logo.svg";
	export let finance;
	export let pinned: string[] = [];
	export let indices = [];
	export let showFuel = true;
	//	{currency|fuel|index: string[]} — tags feeding each tab's "Știri recente"
	export let news = {};
	//	group key of the strip card that opened us: currency | fuel | index
	export let initialTab = "currency";

	const dispatch = createEventDispatcher();
	const FUEL_ORDER = ["benzina_standard", "benzina_premium", "motorina_standard", "motorina_premium", "gpl"];

	let dialog: HTMLDivElement;
	let closeButton: HTMLButtonElement;
	let previousOverflow = "";
	let previouslyFocused: Element | null = null;

	//	pinned currencies first (in strip order), the rest alphabetical by code.
	//	Rates are shown per BNR unit (e.g. 100 HUF) so the table matches the official list.
	$: rates = finance && finance.bnr ? sortRates(finance.bnr.rates, pinned) : [];

	//	the full BNR list is ~37 rows: show the pinned ones + the most common
	//	first, the rest behind a "see all" toggle
	const RATES_COLLAPSED = 8;
	let showAllRates = false;
	$: visibleRates = showAllRates || rates.length <= RATES_COLLAPSED + 1 ? rates : rates.slice(0, RATES_COLLAPSED);
	$: fuels = showFuel && finance && finance.fuel ? FUEL_ORDER.filter((key) => finance.fuel.prices[key]) : [];

	//	only sections that have something to show get a tab; the tablist is
	//	hidden altogether when a single one remains
	const TAB_META = {
		currency: { label: "Curs BNR", source: "Sursă: Banca Națională a României (curs de referință)" },
		fuel: { label: "Carburanți", source: "Sursă: pretcarburant.ro · prețuri medii naționale, lei/litru" },
		index: { label: "Indici", source: "Surse: Banca Națională a României (ROBOR, IRCC)" },
	};
	$: tabs = [
		rates.length ? "currency" : null,
		fuels.length ? "fuel" : null,
		indices.length ? "index" : null,
	].filter(Boolean);

	let tab = null;
	$: if (tabs.length && (!tab || !tabs.includes(tab))) {
		tab = tabs.includes(initialTab) ? initialTab : tabs[0];
	}
	$: newsTags = (tab && news && news[tab]) || [];

	let tabButtons: HTMLButtonElement[] = [];

	function selectTab(key: string) {
		if (key === tab) {
			return;
		}
		tab = key;
		trackFinance("tab", key);
	}

	//	←/→ (and Home/End) move between tabs, WAI-ARIA tabs pattern
	function onTabKeydown(event: KeyboardEvent) {
		const i = tabs.indexOf(tab);
		let next = -1;
		if (event.key === "ArrowRight") next = (i + 1) % tabs.length;
		else if (event.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
		else if (event.key === "Home") next = 0;
		else if (event.key === "End") next = tabs.length - 1;
		if (next < 0) {
			return;
		}
		event.preventDefault();
		selectTab(tabs[next]);
		tabButtons[next]?.focus();
	}

	const reducedMotion =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	const dur = (ms: number) => (reducedMotion ? 0 : ms);

	function sortRates(list, pinned) {
		const rank = {};
		pinned.forEach((code, i) => (rank[code] = i));
		return [...list].sort((a, b) => {
			const ra = rank[a.code] ?? 1000;
			const rb = rank[b.code] ?? 1000;
			if (ra !== rb) {
				return ra - rb;
			}
			return a.code.localeCompare(b.code);
		});
	}

	function trend(current, previous, decimals) {
		if (typeof current !== "number" || typeof previous !== "number" || !isFinite(previous)) {
			return { dir: null, text: "—" };
		}
		const diff = Math.round((current - previous) * Math.pow(10, decimals)) / Math.pow(10, decimals);
		const sign = diff > 0 ? "+" : diff < 0 ? "−" : "";
		return { dir: diff > 0 ? 1 : diff < 0 ? -1 : 0, text: `${sign}${formatNumber(Math.abs(diff), decimals)}` };
	}

	function close() {
		dispatch("close");
	}

	function onKeydown(event: KeyboardEvent) {
		if (event.key === "Tab" && dialog) {
			// Inactive tabs have tabindex=-1: they must not become the trap's endpoints.
			const focusable = Array.from(dialog.querySelectorAll<HTMLElement>('a[href], button, [tabindex]'))
				.filter((element) => element.tabIndex >= 0 && !element.matches(':disabled') &&
					element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (!dialog.contains(document.activeElement)) {
				event.preventDefault(); (event.shiftKey ? last : first)?.focus();
			} else if (event.shiftKey && document.activeElement === first) {
				event.preventDefault(); last?.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault(); first?.focus();
			}
		}
		if (event.key === "Escape") {
			event.stopPropagation();
			close();
		}
	}

	onMount(() => {
		previouslyFocused = document.activeElement;
		previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		if (closeButton) {
			closeButton.focus();
		}
	});

	onDestroy(() => {
		if (typeof document === "undefined") {
			return;
		}
		document.body.style.overflow = previousOverflow;
		if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
			previouslyFocused.focus();
		}
	});
</script>

<svelte:window on:keydown={onKeydown} />

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="backdrop" transition:fade={{ duration: dur(160) }} on:click|self={close}>
	<div
		class="dialog"
		bind:this={dialog}
		role="dialog"
		aria-modal="true"
		aria-labelledby="financeModalTitle"
		transition:fly={{ y: 24, duration: dur(220) }}
	>
		<div class="brandBar">
			<a class="brand" href="/" title={domain}>
				{#if brandLogo}<img src={brandLogo} alt={brandName} width="136" height="24" />{:else}<span>{brandName}</span>{/if}
			</a>
			<button type="button" class="close" aria-label="Închide" bind:this={closeButton} on:click={close}>
				<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
					<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
				</svg>
			</button>
		</div>

		<header>
			<div class="heading">
				<h2 id="financeModalTitle">Curs valutar, carburanți și indici</h2>
				{#if tabs.length > 1}
					<!-- focus lives on the tabs themselves (roving tabindex), not the list -->
					<!-- svelte-ignore a11y-no-noninteractive-tabindex a11y-interactive-supports-focus -->
					<div class="tabs" role="tablist" aria-label="Secțiuni" on:keydown={onTabKeydown}>
						{#each tabs as key, i (key)}
							<button
								type="button"
								role="tab"
								id="financeTab_{key}"
								class="tab"
								class:active={tab === key}
								aria-selected={tab === key}
								aria-controls="financePanel_{key}"
								tabindex={tab === key ? 0 : -1}
								bind:this={tabButtons[i]}
								on:click={() => selectTab(key)}
							>
								{TAB_META[key].label}
							</button>
						{/each}
					</div>
				{/if}
			</div>
		</header>

		<div class="body">
			{#key tab}
				<div
					class="panel"
					role="tabpanel"
					id="financePanel_{tab}"
					aria-labelledby={tabs.length > 1 ? `financeTab_${tab}` : "financeModalTitle"}
					in:fade={{ duration: dur(140) }}
				>
					{#if tab === "currency"}
						<section>
							<div class="sectionHead">
								<h3>Curs BNR</h3>
								<span class="meta">
									{formatShortDate(finance.bnr.date)}
									{#if finance.bnr.prevDate}
										· variație față de {formatShortDate(finance.bnr.prevDate)}
									{/if}
								</span>
							</div>
							<table>
								<thead>
									<tr>
										<th>Monedă</th>
										<th class="num">Curs (lei)</th>
										<th class="num">Variație</th>
									</tr>
								</thead>
								<tbody>
									{#each visibleRates as rate (rate.code)}
										{@const decimals = currencyDecimals(rate.code)}
										{@const t = trend(
											rate.rate * rate.multiplier,
											rate.prev === null ? null : rate.prev * rate.multiplier,
											decimals,
										)}
										<tr class:pinned={pinned.includes(rate.code)}>
											<td>
												<span class="code">{rate.multiplier > 1 ? `${rate.multiplier} ` : ""}{rate.code}</span>
												<span class="name">{CURRENCY_NAMES[rate.code] || ""}</span>
											</td>
											<td class="num value">{formatNumber(rate.rate * rate.multiplier, decimals)}</td>
											<td class="num trend" class:up={t.dir > 0} class:down={t.dir < 0}>{t.text}</td>
										</tr>
									{/each}
								</tbody>
							</table>
							{#if rates.length > visibleRates.length || showAllRates}
								<button type="button" class="more" aria-expanded={showAllRates} on:click={() => {
										showAllRates = !showAllRates;
										trackFinance(showAllRates ? "show_all" : "show_less", "currency");
									}}>
									{showAllRates ? "Arată mai puține" : `Vezi toate monedele (${rates.length})`}
									<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" class:flipped={showAllRates}>
										<path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
									</svg>
								</button>
							{/if}
						</section>
					{:else if tab === "fuel"}
						<section>
							<div class="sectionHead">
								<h3>Prețuri carburanți</h3>
								<span class="meta">medie națională · {formatShortDate(finance.fuel.date)}</span>
							</div>
							<table>
								<thead>
									<tr>
										<th>Carburant</th>
										<th class="num">Minim</th>
										<th class="num">Mediu</th>
										<th class="num">Maxim</th>
										<th class="num">Variație</th>
									</tr>
								</thead>
								<tbody>
									{#each fuels as key (key)}
										{@const price = finance.fuel.prices[key]}
										{@const t = trend(price.avg, price.prevAvg, 2)}
										<tr>
											<td><span class="code">{FUEL_LABELS[key]}</span></td>
											<td class="num">{formatNumber(price.min, 2)}</td>
											<td class="num value">{formatNumber(price.avg, 2)}</td>
											<td class="num">{formatNumber(price.max, 2)}</td>
											<td class="num trend" class:up={t.dir > 0} class:down={t.dir < 0}>{t.text}</td>
										</tr>
									{/each}
								</tbody>
							</table>
							<p class="note">Prețuri în lei/litru.</p>
						</section>
					{:else if tab === "index"}
						<section>
							<div class="sectionHead">
								<h3>Indici de referință</h3>
							</div>
							<ul class="indices">
								{#each indices as index (index.key)}
									{@const t = trend(index.value, index.previous, 2)}
									<li>
										<div class="indexHead">
											<span class="code">{INDEX_LABELS[index.key]}</span>
											<span class="value">{formatNumber(index.value, 2)}<span class="unit">%</span></span>
											<span class="trend" class:up={t.dir > 0} class:down={t.dir < 0}>
												{t.dir === null ? "" : `${t.text} pp`}
											</span>
										</div>
										<p class="desc">
											{INDEX_DESCRIPTIONS[index.key]}{index.date ? ` · valabil de la ${formatShortDate(index.date)}` : ""}
										</p>
									</li>
								{/each}
							</ul>
						</section>
					{/if}

					<NewsList {domain} tags={newsTags} limit={NEWS_LIMIT} section={tab} />
				</div>
			{/key}
		</div>

		<footer>
			{tab ? TAB_META[tab].source : ""}
		</footer>
	</div>
</div>

<style lang="scss">
	.backdrop {
		position: fixed;
		inset: 0;
		z-index: 10000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
		background-color: rgba(10, 20, 35, 0.55);
		backdrop-filter: blur(2px);
		box-sizing: border-box;
	}

	.dialog {
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 680px;
		max-height: min(90vh, 860px);
		background-color: var(--finance-surface, #fff);
		border-radius: 16px;
		box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
		overflow: hidden;
		color: var(--finance-ink, #1a2330);
		font-variant-numeric: tabular-nums;
		font-size: 14px;
		font-family: var(--finance-font, inherit);
		line-height: 1.4;
	}

	//	centred wordmark on its own band, close button pinned to the corner
	.brandBar {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 14px 56px;
		border-bottom: 1px solid var(--finance-border, #e7edf2);
		background-color: var(--finance-subtle, #f8fafc);

		.brand {
			display: inline-block;
			line-height: 0;
			color: inherit;
			text-decoration: none;

			span {
				display: block;
				font-size: 18px;
				font-weight: 700;
				line-height: 1.3;
			}

			img {
				display: block;
				width: 136px;
				height: 24px;
				object-fit: contain;
			}
		}

		.close {
			position: absolute;
			top: 50%;
			right: 12px;
			transform: translateY(-50%);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 36px;
			height: 36px;
			border: none;
			border-radius: 50%;
			background-color: var(--finance-subtle, #eef2f6);
			color: var(--finance-ink, #334);
			cursor: pointer;

			&:hover {
				background-color: var(--finance-hover, #e2e8f0);
			}

			&:focus-visible {
				outline: 2px solid var(--finance-focus, #3b6ea5);
				outline-offset: 2px;
			}
		}
	}

	header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 12px;
		padding: 16px 20px 12px;
		border-bottom: 1px solid var(--finance-border, #e7edf2);

		.heading {
			flex: 1 1 auto;
			min-width: 0;
		}

		h2 {
			margin: 0 0 10px;
			font-size: 18px;
			font-weight: 700;
			letter-spacing: -0.01em;
		}

	}

	//	segmented control: grey track, white active pill
	.tabs {
		display: inline-flex;
		gap: 2px;
		padding: 3px;
		border-radius: 10px;
		background-color: var(--finance-subtle, #f1f5f9);
		max-width: 100%;
		overflow-x: auto;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		.tab {
			flex: 0 0 auto;
			padding: 6px 14px;
			border: none;
			border-radius: 8px;
			background: none;
			color: var(--finance-muted, #556);
			font: inherit;
			font-size: 13px;
			font-weight: 600;
			line-height: 1.2;
			white-space: nowrap;
			cursor: pointer;
			transition:
				background-color 0.15s ease,
				color 0.15s ease,
				box-shadow 0.15s ease;

			&:hover {
				color: var(--finance-ink, #1a2330);
			}

			&.active {
				background-color: var(--finance-surface, #fff);
				color: var(--finance-ink, #1a2330);
				box-shadow: 0 1px 3px rgba(15, 23, 42, 0.14);
			}

			&:focus-visible {
				outline: 2px solid var(--finance-focus, #3b6ea5);
				outline-offset: 1px;
			}
		}
	}

	.body {
		flex: 1 1 auto;
		overflow-y: auto;
		overscroll-behavior: contain;
		padding: 4px 20px 16px;
		-webkit-overflow-scrolling: touch;
	}

	section {
		padding: 12px 0 4px;
	}

	.sectionHead {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 4px 10px;
		margin-bottom: 8px;

		h3 {
			margin: 0;
			font-size: 15px;
			font-weight: 700;
		}

		.meta {
			font-size: 12px;
			color: var(--finance-muted, #667);
		}
	}

	table {
		width: 100%;
		border-collapse: collapse;

		th,
		td {
			padding: 8px 6px;
			text-align: left;
			border-bottom: 1px solid var(--finance-subtle, #eef2f6);
			vertical-align: middle;
		}

		th {
			font-size: 11px;
			font-weight: 600;
			letter-spacing: 0.04em;
			text-transform: uppercase;
			color: var(--finance-muted, #667);
		}

		.num {
			text-align: right;
			white-space: nowrap;
		}

		tr.pinned td {
			background-color: var(--finance-subtle, #f8fafc);
		}

		tbody tr:last-child td {
			border-bottom: none;
		}
	}

	.code {
		font-weight: 700;
	}

	.name {
		display: inline-block;
		margin-left: 8px;
		color: var(--finance-muted, #667);
		font-size: 12.5px;
	}

	.value {
		font-weight: 700;
		font-size: 15px;
	}

	.trend {
		font-weight: 600;
		color: var(--finance-muted, #889);

		&.up {
			color: var(--finance-increase, #b3261e);
		}

		&.down {
			color: var(--finance-decrease, #1b7f3b);
		}
	}

	.more {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-top: 10px;
		padding: 7px 12px;
		border: 1px solid var(--finance-border, #dbe3ea);
		border-radius: 8px;
		background-color: var(--finance-surface, #fff);
		color: var(--finance-ink, #1a2330);
		font: inherit;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;

		&:hover {
			background-color: var(--finance-hover, #f4f7fa);
		}

		&:focus-visible {
			outline: 2px solid var(--finance-focus, #3b6ea5);
			outline-offset: 2px;
		}

		svg {
			transition: transform 0.15s ease;

			&.flipped {
				transform: rotate(180deg);
			}
		}
	}

	.note {
		margin: 8px 0 0;
		font-size: 12px;
		color: var(--finance-muted, #667);
	}

	.indices {
		list-style: none;
		margin: 0;
		padding: 0;

		li {
			padding: 8px 0;

			& + li {
				border-top: 1px solid var(--finance-subtle, #eef2f6);
			}
		}

		.indexHead {
			display: flex;
			align-items: baseline;
			gap: 10px;

			.value {
				font-size: 18px;

				.unit {
					font-size: 12px;
					font-weight: 500;
					color: var(--finance-muted, #667);
					margin-left: 2px;
				}
			}
		}

		.desc {
			margin: 2px 0 0;
			font-size: 12.5px;
			color: var(--finance-muted, #667);
		}
	}

	footer {
		padding: 10px 20px;
		border-top: 1px solid var(--finance-border, #e7edf2);
		background-color: var(--finance-subtle, #f8fafc);
		font-size: 11.5px;
		color: var(--finance-muted, #667);
	}

	//	phones: full-height sheet, no rounded floating card
	@media only screen and (max-width: 600px) {
		.backdrop {
			padding: 0;
			align-items: stretch;
		}

		.dialog {
			max-width: none;
			max-height: none;
			border-radius: 0;
		}

		header,
		.body,
		footer {
			padding-left: 14px;
			padding-right: 14px;
		}

		.brandBar {
			padding: 12px 52px;
		}

		.name {
			display: block;
			margin-left: 0;
			font-size: 11.5px;
		}

		table th,
		table td {
			padding: 6px 4px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.backdrop {
			backdrop-filter: none;
		}
	}
</style>
